<template>
    <div ref="mainContainer" class="relative grid gap-16 md:gap-40">
        <!-- Desktop sticky year display. It stays in one place while entries update its content and flip state. -->
        <div v-if="!editMode && stickyYearVisible" class="hidden md:block absolute inset-y-0 left-0 2xl:ml-[41px]">
            <div
                ref="stickyYear"
                class="sticky top-40 z-1 min-w-[4ch] tabular-nums bg-white text-black-800 md:text-history-year-tablet xl:text-history-year-desktop font-semibold">
                <!-- Expose one stable year value to assistive tech; the animated faces below are visual only. -->
                <span class="sr-only">{{ activeYear }}</span>
                <!-- Remount this inner wrapper for each flip so the CSS animation always starts from a fresh DOM node. -->
                <div :key="stickyAnimationKey" aria-hidden="true" :class="{ flipped: stickyFlipped }">
                    <!-- Face currently visible before the flip. -->
                    <div :class="{ 'flip flip-out': showStickyFlip }">{{ stickyFrontYear }}</div>
                    <!-- Face that flips into view. -->
                    <div v-if="showStickyFlip" class="flip flip-in mt-[-1lh]">{{ activeYear }}</div>
                </div>
            </div>
        </div>
        <slot />
    </div>
    <div ref="timeline" v-if="!editMode" class="hidden 2xl:block absolute inset-y-0 p-4 border-e border-black-200 text-para-xs">
        <div class="sticky top-40">
            <button v-for="year in years" :key="year" class="group w-18 flex items-center gap-1.5" @click="scrollTo(year)">
                <span
                    class="border-b transition-all"
                    :class="{
                        'w-15 border-primary-500': activeYear === year,
                        'w-8 border-black-400 group-hover:border-black-900': activeYear !== year,
                    }"></span>
                <span class="invisible group-hover:visible">{{ activeYear === year ? '&nbsp;' : year }}</span>
            </button>
        </div>
    </div>
    <div
        ref="timelineMobile"
        v-if="!editMode"
        class="2xl:hidden fixed top-full inset-x-0 md:w-120 md:mx-auto p-2 bg-black-50 md:bg-transparent opacity-0">
        <dropdown :white="true" :upward="true" v-model="activeYear">
            <dropdown-item v-for="year in years" :key="`${year}-mobile`" :value="year" :label="year" @gc-item-selected="scrollTo(year)" />
        </dropdown>
    </div>
</template>

<script lang="ts">
import debounce from 'lodash/debounce';
import Dropdown from '../base/Dropdown.vue';
import DropdownItem from '../base/DropdownItem.vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { screens } from '../../plugins/breakpoints';

const MOBILE_TIMELINE_REVEAL_START = 'top 80%';
const MOBILE_TIMELINE_REVEAL_END = 'bottom 40%';

type HistoryItem = {
    year: string;
    element: HTMLElement;
};

export default {
    components: { DropdownItem, Dropdown },
    provide() {
        return {
            register: this.registerItem,
            thePrevYear: this.prevYear,
            setActiveYear: this.setActiveYear,
            // Entry components call these methods when their year becomes active or inactive on scroll.
            flipInYear: this.flipInYear,
            flipOutYear: this.flipOutYear,
        };
    },
    props: {
        editMode: { type: Boolean },
    },
    data() {
        return {
            itemsByYear: {} as Record<string, HistoryItem[]>,
            // Current year across timeline, mobile dropdown, and desktop sticky label.
            activeYear: '',
            // Year currently shown on the front face of the sticky label.
            stickyFrontYear: '',
            // Toggles the CSS class that rotates the sticky year faces.
            stickyFlipped: false,
            // Controls whether the desktop sticky year is visible yet.
            stickyYearVisible: false,
            // Forces Vue to remount the animated wrapper so the flip can replay from a fresh DOM node.
            stickyAnimationKey: 0,
            // Stores if the active year has been initialised
            activeYearInitialised: false,
            // Lifecycle handles for cleaning up responsive triggers and resize observation.
            matchMedia: null as ReturnType<typeof gsap.matchMedia> | null,
            resizeObserver: null as ResizeObserver | null,
        };
    },
    computed: {
        years(): string[] {
            return Object.keys(this.itemsByYear);
        },
        // When the front face and the active year differ, render a two-face flip.
        showStickyFlip(): boolean {
            return !!this.activeYear && this.stickyFrontYear !== this.activeYear;
        },
    },
    mounted() {
        this.positionTimeline();
        gsap.registerPlugin(ScrollTrigger);
        this.setupMobileTimelineTrigger();
        this.setupResizeObserver();
    },
    unmounted() {
        this.matchMedia?.revert();
        this.resizeObserver?.disconnect();
    },
    methods: {
        setupMobileTimelineTrigger() {
            this.matchMedia = gsap.matchMedia();

            // add the scroll trigger that shows and hides the mobile timline dropdown
            this.matchMedia.add(`(max-width: ${screens['2xl']}px)`, () => {
                gsap.to(this.$refs.timelineMobile, {
                    scrollTrigger: {
                        trigger: this.$refs.mainContainer,
                        start: MOBILE_TIMELINE_REVEAL_START,
                        end: MOBILE_TIMELINE_REVEAL_END,
                        toggleActions: 'play reverse play reverse',
                    },
                    opacity: '100%',
                    y: '-100%',
                });
            });
        },
        setupResizeObserver() {
            this.resizeObserver = new ResizeObserver(
                debounce(() => {
                    this.positionTimeline();
                    ScrollTrigger.refresh();
                }, 300),
            );
            this.resizeObserver.observe(document.body);
        },
        // Store entries by year so the timeline can scroll to them and entries can look up the previous year.
        registerItem(item: HistoryItem): boolean {
            const year = item.year;
            let firstItem = false;

            if (!this.itemsByYear[year]) {
                this.itemsByYear[year] = [];
                firstItem = true;
            }

            this.itemsByYear[year].push(item);

            // Initialize the active year for the timeline controls.
            if (!this.activeYearInitialised) {
                // We set the initial active year in the next tick, otherwise the mobile year dropdown watcher
                // misses the change and stays empty.
                // We only do this for the first registered year, which we track with activeYearInitialised
                this.activeYearInitialised = true;
                this.$nextTick(() => (this.activeYear = year));

                // Seed the front face for the first render.
                this.stickyFrontYear = year;
            }

            return firstItem;
        },
        prevYear(year: string): string | undefined {
            const index = this.years.indexOf(year);
            return this.years[index - 1];
        },
        positionTimeline() {
            if (this.$refs.timeline) {
                const relDiv = (this.$refs.timeline as HTMLElement).closest('.relative') as HTMLElement | null;
                if (!relDiv) return;
                const left = relDiv.getBoundingClientRect().left;
                (this.$refs.timeline as HTMLElement).style.left = `-${left}px`;
            }
        },
        scrollTo(year: string) {
            const el = this.itemsByYear[year]?.[0]?.element;
            if (el) {
                el.scrollIntoView();
            }
        },
        setActiveYear(year: string) {
            this.activeYear = year;
        },
        flipInYear(year: string, prevYear?: string) {
            // Show this year in the sticky label. If the front face differs, animate from it into activeYear.
            this.activeYear = year;
            this.stickyYearVisible = true;

            if (!prevYear) {
                // The first year renders as a single face because front and active year are the same.
                this.stickyFrontYear = year;
                this.stickyFlipped = false;
                return;
            }
            // Otherwise the front face shows the previous year, we flip from front to back face -> from prev to current year
            this.stickyFrontYear = prevYear;
            this.restartStickyFlip();
        },
        flipOutYear(prevYear?: string) {
            if (!prevYear) {
                // Scrolling back above the first year hides the sticky label again.
                this.stickyYearVisible = false;
                return;
            }

            // Restore the previous year when scrolling back above a year boundary.
            // activeYear and stickyFrontYear are set to the same value to deactivate the flip
            // animation on scroll up
            this.activeYear = prevYear;
            this.stickyFrontYear = prevYear;
            this.stickyFlipped = false;
            // Replace the animated wrapper so any in-flight flip is discarded.
            this.stickyAnimationKey++;
        },
        restartStickyFlip() {
            // Use the current key as a guard so older queued frames cannot update a newer flip.
            const animationKey = ++this.stickyAnimationKey;
            // Freshly mounted markup starts unflipped.
            this.stickyFlipped = false;

            // Flip on the next frame so the browser paints the initial state before starting the transition.
            // nextTick ensures the unflipped node is in the DOM and requestAnimationFrame ensures it has been
            // painted before we flip it
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    // If another year update started a newer flip in the meantime, ignore this older animation.
                    if (animationKey !== this.stickyAnimationKey) {
                        return;
                    }

                    this.stickyFlipped = true;
                });
            });
        },
    },
};
</script>
